/**
 * Inserts the Bestsellers CTA grid into blog article bodies at the 2nd and
 * 4th "fold" of the actual rendered content — measured after images finish
 * loading so image-driven layout shifts don't throw off the fold math.
 * Insertion only ever happens between top-level content elements, never
 * inside one.
 *
 * Fold height is a fixed pixel constant, NOT window.innerHeight. The
 * Shopify theme editor renders the page inside a preview iframe whose
 * reported height doesn't match the actual browser viewport, and
 * innerHeight also shifts with browser zoom (CSS px per "screen" changes),
 * both of which made viewport-based folds land in wildly different, hard
 * to reproduce places. A fixed constant is deterministic everywhere.
 */
(function () {
  var FOLD_HEIGHT_PX = 900;
  var FOLD_MULTIPLIERS = [2, 4];
  var RESIZE_DEBOUNCE_MS = 250;

  function waitForImages(root) {
    var imgs = Array.prototype.slice.call(root.querySelectorAll('img'));
    if (!imgs.length) return Promise.resolve();
    return Promise.all(
      imgs.map(function (img) {
        if (img.complete) return Promise.resolve();
        return new Promise(function (resolve) {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      })
    );
  }

  function initBlogProductCTA(container, templates) {
    var insertedNodes = [];

    function clearInserted() {
      insertedNodes.forEach(function (node) {
        if (node.parentNode) node.parentNode.removeChild(node);
      });
      insertedNodes = [];
    }

    function place() {
      clearInserted();

      var targets = FOLD_MULTIPLIERS.map(function (multiplier) {
        return multiplier * FOLD_HEIGHT_PX;
      });
      var targetIndex = 0;
      var children = Array.prototype.slice.call(container.children);

      // Each fold gets its own template (a different slice of products)
      // so the CTAs don't repeat the same 4 products back to back. If
      // there are fewer templates than folds, later folds reuse the last
      // available template rather than showing nothing.
      for (var i = 0; i < children.length && targetIndex < targets.length; i++) {
        var child = children[i];
        if (child.offsetTop >= targets[targetIndex]) {
          var template = templates[Math.min(targetIndex, templates.length - 1)];
          var clone = template.content.cloneNode(true);
          var wrapper = clone.firstElementChild;
          container.insertBefore(clone, child);
          insertedNodes.push(wrapper);
          targetIndex++;
        }
      }
      // If the article is shorter than a given fold, that CTA is simply
      // never placed rather than being forced in at the end.
    }

    var resizeTimer;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(place, RESIZE_DEBOUNCE_MS);
    }

    place();
    window.addEventListener('resize', onResize);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var container = document.querySelector('[data-article-content-body]');
    var templates = Array.prototype.slice.call(document.querySelectorAll('[data-blog-product-cta]'));
    if (!container || !templates.length) return;

    waitForImages(container).then(function () {
      initBlogProductCTA(container, templates);
    });
  });

  /**
   * Add-to-cart follows the same pattern as handleAddToCart in
   * assets/quick-view-images.js (commit 749f25b8): this site runs a
   * second, entirely separate cart backend (prod-bifrost.giva-api.com, a
   * "custom cart" A/B test) alongside Shopify's native cart, switched by a
   * "showCustomCart" localStorage flag. Rather than going through
   * <product-form>/product-form.js — whose own submit handler has a real
   * timing bug waiting on a "storage" event that never fires in the same
   * tab that set the flag — this reads the flag directly at click time and
   * branches straight into the matching cart path, same as
   * quick-view-images.js already does for its own add-to-cart button.
   */
  function getCartComponent() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  // Native Shopify cart path — used when showCustomCart is not "true".
  function addToShopifyCart(variantId) {
    var cartElement = getCartComponent();
    var sections =
      cartElement && typeof cartElement.getSectionsToRender === 'function'
        ? cartElement.getSectionsToRender().map(function (section) {
            return section.section || section.id;
          })
        : [];

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) throw new Error(data.description || 'Could not add to cart');
        if (cartElement && typeof cartElement.renderContents === 'function') {
          cartElement.renderContents(data);
        }
      });
  }

  // Custom bifrost cart path — used when showCustomCart is "true". Mirrors
  // quick-view-images.js's addToCustomCart, scoped to a single-item add.
  function addToCustomCart(variantId, productInfo) {
    var userId;
    var refId;

    return Promise.resolve(window.__st && window.__st.cid ? String(window.__st.cid) : window.visitor_id)
      .then(function (resolvedUserId) {
        userId = resolvedUserId;
        return generateCustomCartRefId();
      })
      .then(function (resolvedRefId) {
        refId = resolvedRefId;
        return fetch('https://prod-bifrost.giva-api.com/api/cart/user', {
          headers: { ref_access_token: refId },
        })
          .then(function (res) {
            if (!res.ok) return null;
            return res.json();
          })
          .catch(function () {
            return null;
          });
      })
      .then(function (cartData) {
        var cartId = cartData && cartData.data && cartData.data.cart ? cartData.data.cart.id : undefined;
        if (cartId && cartId.length === 0) cartId = undefined;

        var item = { item_id: String(variantId), quantity: 1 };

        if (!cartId) {
          return fetch('https://prod-bifrost.giva-api.com/api/cart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ref_access_token: refId },
            body: JSON.stringify({
              cart_type: 'primary',
              user_id: userId,
              items: [item],
              payment: { payment_methods: [] },
              benefits: { coupons: [] },
              serviceability_option: { shipments: [] },
              shipping_address: {},
              billing_address: {},
            }),
          });
        }

        return fetch('https://prod-bifrost.giva-api.com/api/cart/update-items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ref_access_token: refId },
          body: JSON.stringify({
            cart_id: cartId,
            user_id: userId,
            items: [{ item: item, operation: 'add' }],
            shipping_address: {},
            billing_address: {},
          }),
        });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (!data.success) throw new Error('Could not add to cart');

        var totalCartItems = data.data.cart.items
          .filter(function (cartItem) {
            return cartItem.item_id != '31471233106010';
          })
          .map(function (cartItem) {
            return cartItem.quantity;
          })
          .reduce(function (a, b) {
            return a + b;
          }, 0);

        document.querySelectorAll('.header__icon--cart').forEach(function (el) {
          if (el.getAttribute('id') === 'cart-icon-bubble') {
            var bubble = el.querySelector('#cart-count-bubble');
            if (bubble) {
              bubble.querySelectorAll('span').forEach(function (span) {
                span.innerHTML = totalCartItems;
              });
            }
          }
        });

        createCustomNotificationPopup({
          product_title: productInfo.title,
          final_line_price: productInfo.price,
          image: productInfo.image,
          totalCartItems: totalCartItems,
        });
      });
  }

  // Reuses the same popup markup/behavior as product-form.js /
  // quick-view-images.js so the confirmation looks identical no matter
  // which flow added the item.
  function createCustomNotificationPopup(item) {
    var existing = document.querySelector('.custom-cart-notification');
    if (existing) existing.remove();

    var popupHTML =
      '<cart-notification class="custom-cart-notification">' +
      '<div class="cart-notification-overlay active" id="cart-notification-overlay">' +
      '<div class="cart-notification-wrapper page-width">' +
      '<div id="cart-notification" class="cart-notification focus-inset color-background-1 gradient animate active" aria-modal="true" aria-label="Item added to your cart" role="dialog" tabindex="-1">' +
      '<div class="cart-notification__header">' +
      '<h2 class="cart-notification__heading caption-large text-body"><svg class="icon icon-checkmark" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 9" fill="none">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"></path>' +
      '</svg>Item added to your cart</h2>' +
      '<button type="button" class="cart-notification__close modal__close-button link link--text focus-inset" aria-label="Close">' +
      '<svg class="icon icon-close" aria-hidden="true" focusable="false"><use href="#icon-close"></use></svg>' +
      '</button>' +
      '</div>' +
      '<div id="cart-notification-product" class="cart-notification-product"><div class="cart-notification-product__image global-media-settings">' +
      '<img src="' + item.image + '" alt="" width="70" height="70" loading="lazy">' +
      '</div><div><h3 class="cart-notification-product__name h4">' + item.product_title + '</h3>' +
      '<h3 class="cart-notification-product__price h4">' + item.final_line_price + '</h3>' +
      '</div></div>' +
      '<div class="cart-notification__links">' +
      '<button id="cart-notification-button-blog-cta" class="button button--secondary button--full-width view-cart-btn">View cart (' + item.totalCartItems + ')</button>' +
      '<button type="button" class="link button-label medium-hide large-up-hide">Continue shopping</button>' +
      '</div></div></div></div></cart-notification>';

    var header = document.body.querySelector('.shopify-section-header-sticky');
    if (!header) return;
    header.insertAdjacentHTML('beforeend', popupHTML);

    var viewCartBtn = document.getElementById('cart-notification-button-blog-cta');
    if (viewCartBtn) {
      viewCartBtn.addEventListener('click', function (e) {
        e.preventDefault();
        Promise.all([
          generateCustomCartRefId(),
          window.__st && window.__st.cid ? Promise.resolve(window.__st.cid) : null,
          window.visitor_id,
        ]).then(function (results) {
          var refId = results[0];
          var userIdForCart = results[1];
          var sessionIdForCart = results[2];
          var gclid = new URLSearchParams(window.location.search).get('gclid') || null;
          var fbclid = new URLSearchParams(window.location.search).get('fbclid') || null;
          var customCartUrl =
            'https://checkout.giva.co/cart?ref_id=' +
            refId +
            '&' +
            (userIdForCart ? 'user_id=' + userIdForCart + '&' : '') +
            (sessionIdForCart ? 'session_id=' + sessionIdForCart + '&' : '') +
            (gclid ? 'gclid=' + gclid + '&' : '') +
            (fbclid ? 'fbclid=' + fbclid + '&' : '');
          window.location.href = customCartUrl;
        });
      });
    }

    var closeBtn = document.querySelector('.custom-cart-notification .cart-notification__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        document.querySelector('.custom-cart-notification').remove();
      });
    }
  }

  function handleAddToCart(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    var spinner = button.querySelector('.loading-overlay__spinner');
    var label = button.querySelector('.blog-product-cta__add-label');
    var addLabel = button.getAttribute('data-add-label') || (label ? label.textContent : '');

    button.disabled = true;
    if (spinner) spinner.classList.remove('hidden');

    var productInfo = {
      title: button.getAttribute('data-product-title') || '',
      price: button.getAttribute('data-product-price') || '',
      image: button.getAttribute('data-product-image') || '',
    };

    var addPromise =
      localStorage.getItem('showCustomCart') === 'true'
        ? addToCustomCart(variantId, productInfo)
        : addToShopifyCart(variantId);

    addPromise
      .then(function () {
        if (label) label.textContent = 'Added!';
        window.setTimeout(function () {
          if (label) label.textContent = addLabel;
        }, 2000);
      })
      .catch(function (error) {
        if (label) label.textContent = 'Couldn’t add';
        window.setTimeout(function () {
          if (label) label.textContent = addLabel;
        }, 2500);
        // eslint-disable-next-line no-console
        console.error('blog-product-cta add to cart failed', error);
      })
      .finally(function () {
        button.disabled = false;
        if (spinner) spinner.classList.add('hidden');
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-blog-cta-add-to-cart]');
    if (!button) return;
    event.preventDefault();
    handleAddToCart(button);
  });
})();
